import { Icon } from '@natera/material/lib/icon'
import { format, parse } from 'date-fns'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { usePaymentSummary, useUpdatePaymentSummary } from '~/entities/document'
import { SuggestedPayorField } from '~/entities/payors'
import { useEobOnboardingSelectors } from '~/features/eob-onboarding'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { useAppForm } from '~/shared/lib/form'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import {
  Button,
  Chip,
  DatePicker,
  IconButton,
  LocalIcon,
  ScrollArea,
  Text,
  Textfield,
  Tooltip,
  toast,
} from '~/shared/ui'
import type { PaymentSummaryFormValues } from '../../model'
import { AdjustmentSheet } from './payment-adjustments/adjustment-sheet'

type PaymentType = apiClient.PaymentSummaryUpdateRequest['payment_type']

/**
 * Phone auto-format. Last 10 digits are the subscriber number (xxx-xxx-xxxx);
 * anything before is the country code. Examples:
 *   8662450815     → 866-245-0815
 *   18662450815    → 1-866-245-0815
 *   218662450815   → 21-866-245-0815
 *   1238662450815  → 123-866-245-0815
 * Capped at 13 digits (covers all real-world numbers).
 */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 13)
  if (digits.length <= 10) {
    if (digits.length < 4) return digits
    if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  const countryCode = digits.slice(0, digits.length - 10)
  const rest = digits.slice(-10)
  return `${countryCode}-${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6)}`
}

function parseCents(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const parsed = typeof value === 'string' ? Number(value) : value
  return Number.isNaN(parsed) ? null : parsed
}

function buildDefaultValues(
  summaryData: apiClient.PaymentSummaryResponse | undefined,
  documentPayorId: string | null | undefined,
): PaymentSummaryFormValues {
  return {
    payorId: documentPayorId ?? '',
    totalPaid: parseCents(summaryData?.total_paid),
    checkDate: summaryData?.check_date ?? '',
    depositDate: summaryData?.deposit_date ?? '',
    checkNumber: summaryData?.check_number ?? '',
    achTraceNumber: summaryData?.ach_trace_number ?? '',
    bankReferenceNumber: summaryData?.bank_reference_number ?? '',
    taxId: summaryData?.tax_id ?? '',
    customerServiceNumber: formatPhone(summaryData?.customer_service_number ?? ''),
    numberOfPatientClaims:
      summaryData?.number_of_patient_claims != null
        ? String(summaryData.number_of_patient_claims)
        : '',
    adjustments: (summaryData?.adjustments ?? []).map((adj) => ({
      key: adj.key ?? null,
      code: adj.code,
      amount: adj.amount ? Number(adj.amount) : null,
      deductFromTotalPaid: adj.deduct_from_total_paid ?? false,
      trackingNumber: adj.tracking_number ?? '',
      notes: adj.notes ?? '',
    })),
  }
}

function ReviewedNotifier({
  isFullyReviewed,
  onReviewedChange,
}: {
  isFullyReviewed: boolean
  onReviewedChange?: (isReviewed: boolean) => void
}) {
  useEffect(() => {
    onReviewedChange?.(isFullyReviewed)
  }, [isFullyReviewed, onReviewedChange])
  return null
}

function ModifiedNotifier({
  isModified,
  onModifiedChange,
}: {
  isModified: boolean
  onModifiedChange?: (isModified: boolean) => void
}) {
  useEffect(() => {
    onModifiedChange?.(isModified)
  }, [isModified, onModifiedChange])
  return null
}

interface PaymentSummaryProps {
  isNonFinancial?: boolean
  onScrolledBottomChange?: (isScrolledBottom: boolean) => void
  onReviewedChange?: (isReviewed: boolean) => void
  onModifiedChange?: (isModified: boolean) => void
  documentPayorId: string | null | undefined
  onPayorChanged: (payorId: string) => Promise<void>
  isPayorUpdatePending: boolean
}

export function PaymentSummary({
  isNonFinancial,
  onScrolledBottomChange,
  onReviewedChange,
  onModifiedChange,
  documentPayorId,
  onPayorChanged,
  isPayorUpdatePending,
}: PaymentSummaryProps) {
  const { data: summaryData, isFetching } = usePaymentSummary()
  const paymentType: PaymentType = summaryData?.payment_type ?? 'check'
  const documentKey = useParams<'documentKey'>().documentKey as string
  const { mutateAsync: updatePaymentSummary, isPending: isSummaryUpdatePending } =
    useUpdatePaymentSummary()
  const { hasSeenSummaryReviewedOnboarding, setIsSummaryReviewedOnboardingOpen } =
    useEobOnboardingSelectors()

  let defaultValues = buildDefaultValues(summaryData, documentPayorId)
  if (isNonFinancial) defaultValues = { ...defaultValues, totalPaid: 0 }

  const form = useAppForm({
    defaultValues,
    validators: {
      onChange: ({ value }) => {
        const errors: Record<string, string> = {}
        if (!isNonFinancial && value.totalPaid == null) errors.totalPaid = 'Required'
        if (!isNonFinancial && paymentType === 'check' && !value.checkNumber)
          errors.checkNumber = 'Required'
        if (!isNonFinancial && paymentType === 'direct_deposit' && !value.achTraceNumber)
          errors.achTraceNumber = 'Required'
        return Object.keys(errors).length ? { fields: errors } : undefined
      },
      onMount: ({ value }) => {
        const errors: Record<string, string> = {}
        if (!isNonFinancial && value.totalPaid == null) errors.totalPaid = 'Required'
        if (!isNonFinancial && paymentType === 'check' && !value.checkNumber)
          errors.checkNumber = 'Required'
        if (!isNonFinancial && paymentType === 'direct_deposit' && !value.achTraceNumber)
          errors.achTraceNumber = 'Required'
        return Object.keys(errors).length ? { fields: errors } : undefined
      },
    },
  })

  const {
    set: setHistoryValues,
    reset: resetHistoryValues,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo({
    initialValues: defaultValues,
    setFieldValue: form.setFieldValue,
  })

  const [sheetOpen, setSheetOpen] = useState(false)
  const { isScrolled: isScrolledTop, sentinelElement: sentinelElementTop } = useIsScrolled()
  const { isScrolled: isScrolledBottom, sentinelElement: sentinelElementBottom } = useIsScrolled()

  useEffect(() => {
    let newDefaults = buildDefaultValues(summaryData, documentPayorId)
    if (isNonFinancial) newDefaults = { ...newDefaults, totalPaid: 0 }
    form.reset(newDefaults)
    resetHistoryValues(newDefaults)
  }, [summaryData, documentPayorId, isNonFinancial]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onScrolledBottomChange?.(isScrolledBottom)
  }, [isScrolledBottom, onScrolledBottomChange])

  const isReviewed = summaryData?.status === 'reviewed'

  const isBusy = isSummaryUpdatePending || isPayorUpdatePending

  async function handleMarkAsReviewed() {
    const values = form.state.values

    const body: apiClient.PaymentSummaryUpdateRequest = {
      payment_type: paymentType,
      total_paid: values.totalPaid,
      check_date: values.checkDate || null,
      deposit_date: values.depositDate || null,
      check_number: values.checkNumber || null,
      ach_trace_number: values.achTraceNumber || null,
      bank_reference_number: values.bankReferenceNumber || null,
      tax_id: values.taxId || null,
      customer_service_number: values.customerServiceNumber || null,
      number_of_patient_claims: values.numberOfPatientClaims
        ? Number(values.numberOfPatientClaims)
        : null,
      status: 'reviewed',
      adjustments: values.adjustments.map((adj) => ({
        key: adj.key ?? undefined,
        code: adj.code,
        amount: adj.amount ?? 0,
        tracking_number: adj.trackingNumber || null,
        notes: adj.notes || null,
        deduct_from_total_paid: adj.deductFromTotalPaid || null,
      })),
      checked_patients: summaryData?.checked_patients ?? undefined,
    }

    try {
      await onPayorChanged(values.payorId)
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body,
      })
      toast(isNonFinancial ? 'Document summary saved' : 'Payment summary saved')
      if (!hasSeenSummaryReviewedOnboarding) {
        setIsSummaryReviewedOnboardingOpen(true)
      }
    } catch {
      // On failure the form remains editable — no action needed.
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <form.Subscribe selector={(state) => state.isDefaultValue}>
        {(isDefaultValue) => (
          <>
            <ReviewedNotifier
              isFullyReviewed={isReviewed && isDefaultValue}
              onReviewedChange={onReviewedChange}
            />
            <ModifiedNotifier
              isModified={!isDefaultValue}
              onModifiedChange={onModifiedChange}
            />
          </>
        )}
      </form.Subscribe>
      <div
        className={cn('flex min-h-[60px] items-center justify-between px-6 py-3', {
          'shadow-popover transition-shadow': isScrolledTop,
        })}
      >
        <div className="flex items-center gap-5">
          {!isNonFinancial && (
            <Text appearance="SUBTITLE2">
              {summaryData.payment_type === 'check' && 'Check'}
              {summaryData.payment_type === 'direct_deposit' && 'Direct Deposit'}
              {summaryData.payment_type === 'credit_card' && 'Credit Card'}
            </Text>
          )}
          <div className="flex items-center gap-3">
            <form.Subscribe selector={(state) => state.isDefaultValue}>
              {(isDefaultValue) => {
                const showReviewed = isReviewed && isDefaultValue
                return (
                  <Chip
                    rounded
                    warning={!showReviewed}
                    success={showReviewed}
                  >
                    {showReviewed ? 'Reviewed' : 'Pending review'}
                  </Chip>
                )
              }}
            </form.Subscribe>
            <form.Subscribe selector={(state) => state.values.adjustments}>
              {(adjustments) => (
                <Button
                  size="small"
                  onClick={() => setSheetOpen(true)}
                  materialIcon={adjustments.length > 0 ? 'edit' : 'add'}
                >
                  Adjustment
                </Button>
              )}
            </form.Subscribe>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip content="Undo">
            <IconButton
              size="small"
              container="small"
              materialIcon="undo"
              title="Undo"
              disabled={!canUndo}
              onClick={undo}
            />
          </Tooltip>
          <Tooltip content="Redo">
            <IconButton
              size="small"
              container="small"
              materialIcon="redo"
              title="Redo"
              disabled={!canRedo}
              onClick={redo}
            />
          </Tooltip>
          <form.Subscribe
            selector={(state) => ({
              isDefaultValue: state.isDefaultValue,
              isValid: state.isValid,
            })}
          >
            {({ isDefaultValue, isValid }) => (
              <Button
                raised
                size="small"
                disabled={(isReviewed && isDefaultValue) || !isValid || isBusy || isFetching}
                onClick={handleMarkAsReviewed}
              >
                Mark as Reviewed
              </Button>
            )}
          </form.Subscribe>
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        {sentinelElementTop}
        <div className="flex flex-col gap-4 px-6 pb-4">
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="payorId">
              {(field) => (
                <SuggestedPayorField
                  label="Payor Name"
                  value={field.state.value}
                  onChange={(value) => {
                    field.handleChange(value)
                    setHistoryValues({ payorId: value })
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            </form.Field>
            <form.Field name="totalPaid">
              {(field) => (
                <Textfield
                  label="Total Paid"
                  currency
                  value={field.state.value ?? undefined}
                  onCentsChange={(cents) => field.handleChange(cents)}
                  onBlur={() => {
                    field.handleBlur()
                    setHistoryValues({ totalPaid: field.state.value })
                  }}
                  required={!isNonFinancial}
                  disabled={isNonFinancial}
                />
              )}
            </form.Field>
          </div>

          {!isNonFinancial && paymentType === 'check' && (
            <div className="grid grid-cols-3 gap-4">
              <form.Field name="checkNumber">
                {(field) => (
                  <Textfield
                    label="Check Number"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={() => {
                      field.handleBlur()
                      setHistoryValues({ checkNumber: field.state.value })
                    }}
                    required
                  />
                )}
              </form.Field>
              <form.Field name="checkDate">
                {(field) => (
                  <DatePicker
                    label="Check Date"
                    date={
                      field.state.value
                        ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                        : undefined
                    }
                    onDateChange={(date) => {
                      const value = date ? format(date, 'yyyy-MM-dd') : ''
                      field.handleChange(value)
                      setHistoryValues({ checkDate: value })
                    }}
                  />
                )}
              </form.Field>
              <form.Field name="depositDate">
                {(field) => (
                  <DatePicker
                    label="Deposit Date"
                    date={
                      field.state.value
                        ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                        : undefined
                    }
                    onDateChange={(date) => {
                      const value = date ? format(date, 'yyyy-MM-dd') : ''
                      field.handleChange(value)
                      setHistoryValues({ depositDate: value })
                    }}
                  />
                )}
              </form.Field>
            </div>
          )}

          {!isNonFinancial && paymentType === 'direct_deposit' && (
            <div className="grid grid-cols-3 gap-4">
              <form.Field name="achTraceNumber">
                {(field) => (
                  <Textfield
                    label="ACH / EFT Number"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={() => {
                      field.handleBlur()
                      setHistoryValues({ achTraceNumber: field.state.value })
                    }}
                    required
                  />
                )}
              </form.Field>
              <form.Field name="bankReferenceNumber">
                {(field) => (
                  <Textfield
                    label="Bank Reference Number"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={() => {
                      field.handleBlur()
                      setHistoryValues({ bankReferenceNumber: field.state.value })
                    }}
                    leadingIcon={
                      <LocalIcon
                        className="text-content-high-emphasis block"
                        name="ia"
                      />
                    }
                  />
                )}
              </form.Field>
              <form.Field name="depositDate">
                {(field) => (
                  <DatePicker
                    label="Deposit Date"
                    date={
                      field.state.value
                        ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                        : undefined
                    }
                    onDateChange={(date) => {
                      const value = date ? format(date, 'yyyy-MM-dd') : ''
                      field.handleChange(value)
                      setHistoryValues({ depositDate: value })
                    }}
                  />
                )}
              </form.Field>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div className="relative">
              <Tooltip content="Count the claims in the EOB and enter the total for progress tracking.">
                <Icon
                  children="info"
                  className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
                />
              </Tooltip>
              <form.Field name="numberOfPatientClaims">
                {(field) => (
                  <Textfield
                    label="Number of Patient Claims"
                    leadingIcon={<span />}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={() => {
                      field.handleBlur()
                      setHistoryValues({ numberOfPatientClaims: field.state.value })
                    }}
                  />
                )}
              </form.Field>
            </div>
            <form.Field name="customerServiceNumber">
              {(field) => (
                <Textfield
                  label="Customer Service Phone"
                  value={field.state.value}
                  onChange={(e) => {
                    const next = formatPhone(e.target.value)
                    // Reject insertions once max digits reached — user must delete first.
                    const current = field.state.value
                    const currentDigits = current.replace(/\D/g, '')
                    const nextDigits = next.replace(/\D/g, '')
                    if (currentDigits.length >= 13 && nextDigits.length >= currentDigits.length) {
                      return
                    }
                    field.handleChange(next)
                  }}
                  onBlur={() => {
                    field.handleBlur()
                    setHistoryValues({ customerServiceNumber: field.state.value })
                  }}
                />
              )}
            </form.Field>
            <form.Field name="taxId">
              {(field) => (
                <Textfield
                  label="Tax ID (TIN)"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={() => {
                    field.handleBlur()
                    setHistoryValues({ taxId: field.state.value })
                  }}
                />
              )}
            </form.Field>
          </div>
        </div>

        {sentinelElementBottom}
      </ScrollArea>

      <AdjustmentSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        initialAdjustments={form.state.values.adjustments}
        onSave={(adjustments) => {
          form.setFieldValue('adjustments', adjustments)
          setHistoryValues({ adjustments })
        }}
      />
    </div>
  )
}
